import React, { useState, useMemo, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  ThemeProvider,
  Paper,
  Typography,
  Stack,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
} from '@mui/material';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAPI } from '../Hooks/useAPIs';
import { useCurrentUser } from '../Hooks/useCurrentUser';
import { TimezoneContext } from '../common/TimezoneContext/TimezoneContext';
import { RegistrationCard } from './components/RegistrationCard';
import './DeviceView.css';

const REQUEST_TYPE_OPTIONS = [
  { value: '', label: 'All Request Types' },
  { value: 'ownership_transfer', label: 'Ownership Transfer' },
  { value: 'new_device', label: 'New Device' },
  { value: 'registration_renewal', label: 'Registration Renewal' },
  { value: 'tailscale_approval', label: 'Tailscale Approval' },
  { value: 'device_lost', label: 'Device Lost' },
  { value: 'device_replacement', label: 'Device Replacement' },
  { value: 'device_return', label: 'Device Return' },
];

const UserPendingRegistrationsView = ({ activeTheme }) => {
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [pendingRequestTypeFilter, setPendingRequestTypeFilter] = useState('');
  const [selectedRegistration, setSelectedRegistration] = useState(null);

  const { apiCall } = useAPI();
  const { isAuthenticated } = useCurrentUser();
  const queryClient = useQueryClient();
  const { formatDateTime } = useContext(TimezoneContext);

  const pendingRegistrationsQuery = useQuery({
    queryKey: ['user-pending-requests', pagination.pageIndex, pagination.pageSize, pendingRequestTypeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('status', 'pending');
      params.append('page', (pagination.pageIndex + 1).toString());
      params.append('page_size', pagination.pageSize.toString());
      if (pendingRequestTypeFilter) {
        params.append('request_type', pendingRequestTypeFilter);
      }
      const response = await apiCall('GET', `api/v1/devices/requests?${params.toString()}`);
      return response.data;
    },
    enabled: isAuthenticated,
    keepPreviousData: true,
  });

  const pendingResults = useMemo(() => {
    return pendingRegistrationsQuery.data?.results || [];
  }, [pendingRegistrationsQuery.data]);

  const handleViewDetails = (registration) => {
    setSelectedRegistration(registration);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['user-pending-requests'] });
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider theme={activeTheme}>
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            Please log in to view your pending registrations.
          </Typography>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={activeTheme}>
      <Box className="device-main-container">
        <Paper elevation={2} className="pending-requests-panel">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box className="pending-requests-header">
              <PendingActionsIcon className="pending-requests-header-icon" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                My Pending Requests
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              size="small"
              disabled={pendingRegistrationsQuery.isFetching}
            >
              Refresh
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Filter by Request Type</InputLabel>
              <Select
                value={pendingRequestTypeFilter}
                label="Filter by Request Type"
                onChange={(e) => {
                  setPendingRequestTypeFilter(e.target.value);
                  setPagination({ ...pagination, pageIndex: 0 });
                }}
              >
                {REQUEST_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {pendingRegistrationsQuery.data?.count !== undefined && (
              <Typography variant="body2" color="text.secondary">
                Total: {pendingRegistrationsQuery.data.count} pending request{pendingRegistrationsQuery.data.count !== 1 ? 's' : ''}
              </Typography>
            )}
          </Box>

          <Divider className="pending-requests-divider" />

          {pendingRegistrationsQuery.isFetching && pendingResults.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5 }}>
              <CircularProgress />
            </Box>
          ) : pendingResults.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <PendingActionsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No Pending Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pendingRequestTypeFilter
                  ? `No pending ${REQUEST_TYPE_OPTIONS.find(o => o.value === pendingRequestTypeFilter)?.label || 'requests'} found.`
                  : 'You have no pending device requests at this time.'}
              </Typography>
            </Box>
          ) : (
            <>
              <Stack spacing={2}>
                {pendingResults.map((registration) => (
                  <RegistrationCard
                    key={registration.id}
                    registration={registration}
                    formatDateTime={formatDateTime}
                    onDetails={handleViewDetails}
                  />
                ))}
              </Stack>
              {pendingRegistrationsQuery.data?.count > pendingResults.length && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setPagination({ ...pagination, pageIndex: Math.max(0, pagination.pageIndex - 1) })}
                    disabled={pagination.pageIndex === 0 || pendingRegistrationsQuery.isFetching}
                  >
                    Previous
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Page {pagination.pageIndex + 1} - Showing {pendingResults.length} of {pendingRegistrationsQuery.data?.count || 0} requests
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setPagination({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
                    disabled={
                      !pendingRegistrationsQuery.data?.next ||
                      pendingRegistrationsQuery.isFetching ||
                      pendingResults.length < pagination.pageSize
                    }
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Box>
    </ThemeProvider>
  );
};

export default UserPendingRegistrationsView;

